import type { Location } from "@/modules/locations/locations"
import type { SimpleSelectOptions } from "@/modules/uiKitOverrides/SelectFilter/Select"

import { useMutation } from "@tanstack/react-query"
import pDebounce from "p-debounce"
import { useMemo } from "react"

import { useLocale } from "@/modules/locales/useLocale"
import { DEBOUNCE_DELAY, QUERY_CACHE_TIME } from "@/modules/locations/constants"
import {
  localitiesAutocomplete,
  localitiesAutocompleteKey,
  localityDetails,
  localityDetailsKey,
} from "@/modules/woosmap/endpoints/localities"
import { getLocalityLevelsByTypes } from "@/modules/woosmap/utils/getLocalityLevelsByTypes"

type LocationType = {
  city: string
  country: string
  formattedAddress: string
  latitude: number
  longitude: number
  placeId: string
  state: string
  subState: string
}

type UseWoosmapLocation = {
  fetchPredictionsDebounced: (
    input: string,
    options: {
      onSuccess: (predictions: SimpleSelectOptions[]) => void
      onZeroResult?: () => void
      onError?: () => void
      onSettled?: () => void
    }
  ) => Promise<void>
  fetchDetails: (
    placeId: string,
    options: {
      onSuccess: (location: Location) => void
      onError: () => void
    }
  ) => void
}

type UseAutocompleteService = (searchValue: string) => Promise<SimpleSelectOptions[]>
const useAutocompleteService = (apiKey: string): UseAutocompleteService => {
  const locale = useLocale()

  const { mutateAsync } = useMutation({
    gcTime: QUERY_CACHE_TIME,
    mutationFn: localitiesAutocomplete,
    mutationKey: [localitiesAutocompleteKey],
  })

  return async (searchValue: string) => {
    const results = await mutateAsync({
      key: apiKey,
      language: locale,
      query: searchValue,
      types: ["country", "locality", "admin_level"],
    })

    const options: SimpleSelectOptions[] = results.predictions.map(prediction => {
      return {
        label: prediction.description,
        value: prediction.publicId,
      }
    })

    return Promise.resolve(options)
  }
}

type UsePlacesService = (publicId: string) => Promise<LocationType>
const usePlacesService = (apiKey: string): UsePlacesService => {
  const { mutateAsync } = useMutation({
    gcTime: QUERY_CACHE_TIME,
    mutationFn: localityDetails,
    mutationKey: [localityDetailsKey],
  })

  return async (publicId: string) => {
    const { result } = await mutateAsync({
      key: apiKey,
      publicId,
    })

    const { country, state, subState, city } = getLocalityLevelsByTypes(result)

    const location: LocationType = {
      city,
      country,
      formattedAddress: result.formattedAddress,
      latitude: result.geometry.location.lat,
      longitude: result.geometry.location.lng,
      placeId: result.publicId,
      state,
      subState,
    }

    return location
  }
}

export const useWoosmapLocation = (apiKey: string): UseWoosmapLocation => {
  const autocompleteService = useAutocompleteService(apiKey)
  const placesService = usePlacesService(apiKey)

  const fetchPredictionsDebounced = useMemo(() => {
    return pDebounce(
      (
        input: string,
        options: {
          onSuccess: (predictions: SimpleSelectOptions[]) => void
          onZeroResult?: () => void
          onError?: () => void
          onSettled?: () => void
        }
      ): void => {
        if (input === "") {
          return
        }

        autocompleteService(input)
          .then(predictions => {
            if (predictions.length === 0) {
              options.onZeroResult?.()
            }

            options.onSuccess(predictions)
            options.onSettled?.()
          })
          .catch(() => {
            options.onError?.()
          })
      },
      DEBOUNCE_DELAY
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchDetails = (
    placeId: string,
    options: {
      onSuccess: (location: Location) => void
      onError: () => void
    }
  ): void => {
    if (!placesService) {
      return
    }

    placesService(placeId)
      .then(location => {
        options.onSuccess(location)
      })
      .catch(() => {
        options.onError()
      })
  }

  return { fetchDetails, fetchPredictionsDebounced }
}
